/**
 * Utility to generate an offline/self-contained static map Data URL
 * using Web Mercator tile stitching on an HTML5 canvas.
 * Uses CartoDB Voyager tiles which support public CORS.
 */
export async function generateStaticMapDataUrl(
  lat: number,
  lng: number,
  options?: {
    width?: number;
    height?: number;
    zoom?: number;
    title?: string;
  },
): Promise<string> {
  if (typeof document === 'undefined') return '';

  const width = options?.width ?? 750;
  const height = options?.height ?? 260;
  const zoom = options?.zoom ?? 15;
  const title = options?.title || 'Emplacement du bien';

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';

  // Background fallback
  ctx.fillStyle = '#e2e8f0';
  ctx.fillRect(0, 0, width, height);

  // Web Mercator calculations (EPSG:3857)
  const n = Math.pow(2, zoom);
  const centerX = ((lng + 180) / 360) * n;
  const latRad = (lat * Math.PI) / 180;
  const centerY = ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n;

  const worldPixelX = centerX * 256;
  const worldPixelY = centerY * 256;

  const originX = worldPixelX - width / 2;
  const originY = worldPixelY - height / 2;

  const minTileX = Math.floor(originX / 256);
  const maxTileX = Math.floor((originX + width) / 256);
  const minTileY = Math.floor(originY / 256);
  const maxTileY = Math.floor((originY + height) / 256);

  const tilePromises: Promise<{ img: HTMLImageElement; dx: number; dy: number } | null>[] = [];
  const subdomains = ['a', 'b', 'c', 'd'];

  for (let tx = minTileX; tx <= maxTileX; tx++) {
    for (let ty = minTileY; ty <= maxTileY; ty++) {
      const dx = tx * 256 - originX;
      const dy = ty * 256 - originY;
      const s = subdomains[Math.abs(tx + ty) % subdomains.length];
      const tileUrl = `https://${s}.basemaps.cartocdn.com/rastertiles/voyager/${zoom}/${tx}/${ty}.png`;

      const p = new Promise<{ img: HTMLImageElement; dx: number; dy: number } | null>((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve({ img, dx, dy });
        img.onerror = () => {
          // Fallback to OSM directly if Carto tile fails
          const osmImg = new Image();
          osmImg.crossOrigin = 'anonymous';
          osmImg.onload = () => resolve({ img: osmImg, dx, dy });
          osmImg.onerror = () => resolve(null);
          osmImg.src = `https://${s}.tile.openstreetmap.org/${zoom}/${tx}/${ty}.png`;
        };
        img.src = tileUrl;
      });
      tilePromises.push(p);
    }
  }

  let drawnTiles = 0;
  try {
    const loadedTiles = await Promise.race([
      Promise.all(tilePromises),
      new Promise<null[]>((resolve) => setTimeout(() => resolve([]), 3500)),
    ]);

    for (const t of loadedTiles) {
      if (t) {
        ctx.drawImage(t.img, t.dx, t.dy, 256, 256);
        drawnTiles++;
      }
    }
  } catch {
    // Keep clean background
  }

  // If no tiles could be loaded, draw a nice vector map grid pattern
  if (drawnTiles === 0) {
    ctx.fillStyle = '#f1f5f9';
    ctx.fillRect(0, 0, width, height);

    // Decorative grid roads
    ctx.strokeStyle = '#e2e8f0';
    ctx.lineWidth = 12;
    ctx.beginPath();
    ctx.moveTo(0, height * 0.45);
    ctx.bezierCurveTo(width * 0.3, height * 0.4, width * 0.6, height * 0.6, width, height * 0.5);
    ctx.stroke();

    ctx.lineWidth = 8;
    ctx.beginPath();
    ctx.moveTo(width * 0.4, 0);
    ctx.bezierCurveTo(width * 0.45, height * 0.4, width * 0.55, height * 0.7, width * 0.6, height);
    ctx.stroke();

    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 6;
    ctx.stroke();
  }

  // Draw Pin & Pulse at center (width / 2, height / 2)
  const cx = width / 2;
  const cy = height / 2;

  // Pulse halo
  ctx.beginPath();
  ctx.arc(cx, cy, 30, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(79, 70, 229, 0.15)';
  ctx.fill();

  ctx.beginPath();
  ctx.arc(cx, cy, 16, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(79, 70, 229, 0.28)';
  ctx.fill();

  // Pin outer circle
  ctx.beginPath();
  ctx.arc(cx, cy, 10, 0, Math.PI * 2);
  ctx.fillStyle = '#4f46e5';
  ctx.fill();
  ctx.lineWidth = 3;
  ctx.strokeStyle = '#ffffff';
  ctx.stroke();

  // Center white dot
  ctx.beginPath();
  ctx.arc(cx, cy, 3.5, 0, Math.PI * 2);
  ctx.fillStyle = '#ffffff';
  ctx.fill();

  // Small Pill Label above the pin
  ctx.font = 'bold 10px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
  const textWidth = ctx.measureText(title).width;
  const pillW = textWidth + 18;
  const pillH = 22;
  const pillX = cx - pillW / 2;
  const pillY = cy - 38;

  ctx.save();
  ctx.shadowColor = 'rgba(0, 0, 0, 0.18)';
  ctx.shadowBlur = 6;
  ctx.shadowOffsetY = 2;
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.roundRect(pillX, pillY, pillW, pillH, 11);
  ctx.fill();
  ctx.restore();

  ctx.lineWidth = 1.5;
  ctx.strokeStyle = '#c7d2fe';
  ctx.beginPath();
  ctx.roundRect(pillX, pillY, pillW, pillH, 11);
  ctx.stroke();

  ctx.fillStyle = '#3730a3';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(title, cx, pillY + pillH / 2);

  // Small attribution badge in bottom right corner
  const attrText = '© OpenStreetMap, © CARTO';
  ctx.font = '9px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
  const attrW = ctx.measureText(attrText).width + 8;
  ctx.fillStyle = 'rgba(255, 255, 255, 0.85)';
  ctx.fillRect(width - attrW - 4, height - 16, attrW, 14);
  ctx.fillStyle = '#64748b';
  ctx.textAlign = 'right';
  ctx.textBaseline = 'bottom';
  ctx.fillText(attrText, width - 8, height - 3);

  try {
    return canvas.toDataURL('image/jpeg', 0.92);
  } catch {
    // If canvas was somehow tainted by an external tile, return a guaranteed clean vector canvas
    const fallbackCanvas = document.createElement('canvas');
    fallbackCanvas.width = width;
    fallbackCanvas.height = height;
    const fctx = fallbackCanvas.getContext('2d');
    if (fctx) {
      fctx.fillStyle = '#f8fafc';
      fctx.fillRect(0, 0, width, height);
      fctx.fillStyle = '#4f46e5';
      fctx.beginPath();
      fctx.arc(cx, cy, 10, 0, Math.PI * 2);
      fctx.fill();
      fctx.fillStyle = '#ffffff';
      fctx.beginPath();
      fctx.arc(cx, cy, 3.5, 0, Math.PI * 2);
      fctx.fill();
      fctx.font = 'bold 10px sans-serif';
      fctx.fillStyle = '#1e293b';
      fctx.textAlign = 'center';
      fctx.fillText(title, cx, cy - 18);
      return fallbackCanvas.toDataURL('image/jpeg', 0.9);
    }
    return '';
  }
}
